// Step 6 of PLAN_AUTH.md — Auth0-backed TeamProvider.
//
// Preserves the hook contract from the original mock-auth implementation
// so the 5 callers (App.jsx, CommentsPanel, DocumentSection,
// NotificationBell, Timeline) don't need to change:
//   useTeam()        -> { team, currentUser, signIn, signOut }
//   useCurrentUser() -> { id, name, email, initials, color } | null
//   getTeamUser(id)  -> same shape | null
//
// Two regimes, branched on AUTH0_CONFIGURED:
//   - configured  : currentUser derived from Auth0 user claim; team is []
//                   until we wire /api/v1/orgs/me/members. signIn/signOut
//                   delegate to Auth0's loginWithRedirect / logout.
//   - unconfigured: legacy hardcoded team picker (so local dev without an
//                   Auth0 tenant still works for testing comments/etc).
//
// One-time cleanup: the 'seidr.currentUserId' localStorage key from the
// mock-auth era is removed on first mount when Auth0 is configured, so a
// stale dropdown user can't shadow the real Auth0 identity.
//
// User-id shape change is breaking by design: old JSONB refs ('u_ben' etc.)
// won't resolve via getTeamUser and will render as "Unknown user" until
// the comments[].user_id backfill runs. This is acceptable degradation
// per PLAN_AUTH Step 6.
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';

const AUTH0_DOMAIN = import.meta.env?.VITE_AUTH0_DOMAIN || '';
const AUTH0_CONFIGURED = AUTH0_DOMAIN && !AUTH0_DOMAIN.includes('your-tenant');

// Local-dev fallback. Only used when Auth0 isn't configured (i.e. no
// VITE_AUTH0_DOMAIN). Mirrors the pre-Step-6 TEAM constant verbatim so
// existing JSONB references still resolve in dev.
const FALLBACK_TEAM = [
  { id: 'u_ben',    name: 'Ben Rastum', email: 'ben@seidr.no',    initials: 'BR', color: '#22d3ee' },
  { id: 'u_simon',  name: 'Simon',      email: 'simon@vanir.no',  initials: 'SI', color: '#a78bfa' },
  { id: 'u_kati',   name: 'Kati',       email: 'kati@vanir.no',   initials: 'KA', color: '#f59e0b' },
  { id: 'u_erlend', name: 'Erlend',     email: 'erlend@vanir.no', initials: 'ER', color: '#34d399' },
];

// Back-compat re-export. Nothing imports TEAM today, but the plan
// promised to keep it during the transition window.
export const TEAM = FALLBACK_TEAM;

const LS_KEY_LEGACY = 'seidr.currentUserId';

// Module-level cache so the (non-hook) getTeamUser() export can resolve
// IDs without a React context. Populated by TeamProvider as users flow
// through. Cleared on sign-out so it can't leak identity across sessions.
const _teamCache = new Map();

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '??';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Deterministic palette pick from a stable id (Auth0 sub) so each user
// keeps the same avatar color across sessions / machines. djb2-style hash.
function deriveColor(id) {
  const palette = [
    '#22d3ee', '#a78bfa', '#f59e0b', '#34d399',
    '#f472b6', '#60a5fa', '#fb7185', '#a3e635',
  ];
  let h = 5381;
  for (let i = 0; i < id.length; i++) h = ((h * 33) ^ id.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

const TeamContext = createContext(null);

export function TeamProvider({ children }) {
  // useAuth0 returns sensible defaults (isAuthenticated=false, user=undefined)
  // when there's no Auth0Provider above us — no need to guard the call.
  // We deliberately don't read isLoading here: when Auth0 isn't configured
  // App.jsx skips Auth0Provider entirely and isLoading would stay `true`
  // forever, blocking the fallback path. isAuthenticated/user are accurate
  // either way.
  const { isAuthenticated, user, loginWithRedirect, logout } = useAuth0();

  // ── One-time legacy LS cleanup ────────────────────────────────────
  // Run exactly once per mount (StrictMode-safe via the ref guard).
  const cleanedRef = useRef(false);
  useEffect(() => {
    if (!AUTH0_CONFIGURED || cleanedRef.current) return;
    cleanedRef.current = true;
    try { localStorage.removeItem(LS_KEY_LEGACY); } catch {}
  }, []);

  // ── Fallback (no-Auth0) team-picker state ─────────────────────────
  // Persists across reloads in localStorage so dev refreshes don't reset.
  const [fallbackUserId, setFallbackUserId] = useState(() => {
    if (AUTH0_CONFIGURED) return null;
    try { return localStorage.getItem(LS_KEY_LEGACY) || null; } catch { return null; }
  });
  useEffect(() => {
    if (AUTH0_CONFIGURED) return;
    try {
      if (fallbackUserId) localStorage.setItem(LS_KEY_LEGACY, fallbackUserId);
      else                localStorage.removeItem(LS_KEY_LEGACY);
    } catch {}
  }, [fallbackUserId]);

  // ── Current user ──────────────────────────────────────────────────
  const currentUser = useMemo(() => {
    if (AUTH0_CONFIGURED) {
      if (!isAuthenticated || !user) return null;
      return {
        id: user.sub,                                  // e.g. 'auth0|abc123'
        name: user.name || user.email || 'Authenticated user',
        email: user.email || '',
        initials: deriveInitials(user.name || user.email),
        color: deriveColor(user.sub || ''),
        picture: user.picture,
      };
    }
    return fallbackUserId
      ? FALLBACK_TEAM.find(u => u.id === fallbackUserId) || null
      : null;
  }, [isAuthenticated, user, fallbackUserId]);

  // ── Team list ──────────────────────────────────────────────────────
  // Auth0 path: empty array until we wire /api/v1/orgs/me/members
  // (follow-on ticket). @-mention pickers degrade gracefully — they just
  // won't show suggestions. Fallback path: hardcoded list, as before.
  const team = AUTH0_CONFIGURED ? [] : FALLBACK_TEAM;

  // Keep the module-level cache in sync so getTeamUser() can resolve
  // both the current user and any teammates we know about.
  useEffect(() => {
    if (currentUser) _teamCache.set(currentUser.id, currentUser);
    for (const u of team) _teamCache.set(u.id, u);
  }, [currentUser, team]);

  // ── Sign in / out ──────────────────────────────────────────────────
  const signIn = useCallback(
    (userIdOrUndefined) => {
      if (AUTH0_CONFIGURED) {
        loginWithRedirect();
      } else if (userIdOrUndefined) {
        setFallbackUserId(userIdOrUndefined);
      }
    },
    [loginWithRedirect],
  );
  const signOut = useCallback(() => {
    if (AUTH0_CONFIGURED) {
      _teamCache.clear();
      logout({ logoutParams: { returnTo: window.location.origin } });
    } else {
      setFallbackUserId(null);
    }
  }, [logout]);

  return (
    <TeamContext.Provider value={{ team, currentUser, signIn, signOut }}>
      {children}
    </TeamContext.Provider>
  );
}

export function useTeam() {
  const ctx = useContext(TeamContext);
  if (!ctx) throw new Error('useTeam must be used within TeamProvider');
  return ctx;
}

export function useCurrentUser() {
  return useTeam().currentUser;
}

// Lookup helper for resolving user IDs (from comment mentions,
// workstream owner_user_id, etc.) back to display info. Returns null
// for unknown IDs — including the legacy 'u_ben'-style IDs in JSONB
// columns that pre-date the Auth0 cutover. Consumers already handle
// null (CommentsPanel shows "Unknown user").
export function getTeamUser(userId) {
  if (!userId) return null;
  return _teamCache.get(userId) || null;
}
